import React, { useEffect, useMemo, useState } from "react";

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 🔹 Keep HIGH video quality (no eco compression)
  const videoUrls = useMemo(
    () => ({
      mobile:
        "https://res.cloudinary.com/ddvpczzng/video/upload/" +
        "f_auto:video,q_auto/" +
        "v1765911528/violet_mobile_zcwcny",

      desktop:
        "https://res.cloudinary.com/ddvpczzng/video/upload/" +
        "f_auto:video,q_auto/" +
        "v1765909277/lv_0_20251216225158_gfuqvh",
    }),
    []
  );

  // 🔹 Safe preload (same as first code)
  useEffect(() => {
    if (typeof document === "undefined") return;

    const videoUrl = isMobile ? videoUrls.mobile : videoUrls.desktop;
    const link = document.createElement("link");

    link.rel = "preload";
    link.as = "video";
    link.href = `${videoUrl}.mp4`;
    link.crossOrigin = "anonymous";

    document.head.appendChild(link);
    return () =>
      document.head.contains(link) && document.head.removeChild(link);
  }, [isMobile, videoUrls]);

  const videoSrc = isMobile ? videoUrls.mobile : videoUrls.desktop;

  return (
    <section
      id="home"
      aria-label="Hero section"
      className="relative w-full h-[100dvh] min-h-[600px] overflow-hidden flex items-center justify-center"
    >
      <h1 className="sr-only">STVMHSS School Campus — Arise and Shine</h1>

      <div className="absolute inset-0 z-0" aria-hidden="true">
        <div
          className={`absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 transition-opacity duration-500 ${
            videoLoaded ? "opacity-0" : "opacity-100"
          }`}
        />

        <video
          key={isMobile ? "mobile" : "desktop"}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover motion-reduce:hidden"
          poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%231e1b4b'/%3E%3C/svg%3E"
          onLoadedData={() => setVideoLoaded(true)}
        >
          <source src={`${videoSrc}.mp4`} type="video/mp4" />
          <source src={`${videoSrc}.webm`} type="video/webm" />
        </video>

        <div className="absolute inset-0 hidden motion-reduce:block bg-gradient-to-br from-slate-900 via-purple-900/30 to-slate-900" />
      </div>

      <div
        className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70 z-10"
        aria-hidden="true"
      />

      <div className="absolute bottom-4 left-4 z-20">
        <div className="relative">
          <div className="absolute -inset-1 bg-cyan-600 rounded-lg blur opacity-70 animate-pulse" />
          <div className="relative px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
            <span className="text-white text-base font-serif font-medium tracking-wide">
              Arise and Shine
            </span>
          </div>
        </div>
      </div>

      <div className="absolute top-20 left-10 w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-cyan-400/20 blur-xl animate-[float_8s_ease-in-out_infinite] motion-reduce:animate-none" />
      <div className="absolute bottom-1/3 right-10 w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-white/10 blur-xl animate-[float_10s_ease-in-out_infinite_reverse] motion-reduce:animate-none" />
    </section>
  );
};

export default React.memo(HeroSection);
